const VIDEO_DESTINATION_URL = 'https://api.vimeo.com/me/projects/5400740/videos';

/**
 * Creates a video upload in Vimeo using the form upload method in Vimeo.
 *
 * @param {File} fileSize - The size of the video file to be uploaded.
 * @param {string} videoName - The name of the created video.
 * @param {string} coachId - The coach ID.
 * @param {firebase.remote.RemoteType} remote - The Firebase remote object for Firebase-related operations.
 * @returns {Promise<Object>} - Returns the response from the API with the video data.
 */
const fetchUploadLink = async (fileSize, videoName, coachId, remote) => {
  const response = await remote('generateVimeoUpload', {
    fileSize,
    videoName,
    coachId,
  });

  if (!response.ok) {
    throw new Error(`Received an erroneous response while creating the video upload (${response.status})`);
  }

  const {
    videoURI,
    uploadData: {
      upload_link: uploadLink,
    },
  } = await response.json();

  if (!uploadLink) {
    throw new Error('Upload link has not been created');
  }

  return ({ videoURI, uploadLink });
};

/**
 * Uploads a video file to the provided Vimeo upload link.
 *
 * Implemented with XMLHttpRequest (not fetch) so the byte upload is genuinely
 * awaitable, reports upload progress, and can be aborted. The returned promise
 * resolves when Vimeo has received the whole file and rejects on network error,
 * abort, or a non-2xx response — previously this was a fire-and-forget fetch
 * that resolved the moment the request was dispatched, so upload failures were
 * silently swallowed.
 *
 * @param {string} uploadLink - The Vimeo upload link received after creating the video placeholder on Vimeo.
 * @param {File} file - The video file to be uploaded.
 * @param {Object} [options] - Optional upload hooks.
 * @param {function(number, {loaded: number, total: number}): void} [options.onProgress] - Called with 0-100 and the
 *   raw byte counts as bytes are sent (byte counts enable upload-speed/ETA estimates).
 * @param {function(function): void} [options.onAbortHandle] - Receives a function that aborts the upload.
 * @returns {Promise<void>} Resolves once the upload completes successfully.
 */
const uploadVideo = (uploadLink, file, { onProgress, onAbortHandle } = {}) => (
  new Promise((resolve, reject) => {
    // Vimeo upload link expects the data to be in the 'formdata' format and the key as 'file_data'
    const formData = new FormData();
    formData.append('file_data', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', uploadLink);
    xhr.setRequestHeader('Accept', 'application/vnd.vimeo.*+json;version=3.4');

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100), { loaded: event.loaded, total: event.total });
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Video upload failed with status ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Video upload failed due to a network error'));
    xhr.onabort = () => reject(new Error('Video upload was cancelled'));

    if (onAbortHandle) {
      onAbortHandle(() => xhr.abort());
    }

    xhr.send(formData);
  })
);

/**
 * Extracts the numeric video id from a Vimeo video URI (e.g. "/videos/123456" -> "123456").
 *
 * @param {string} videoURI - The video URI returned by generateVimeoUpload.
 * @returns {string} The Vimeo video id.
 */
const getVideoIdFromURI = (videoURI) => videoURI.slice(videoURI.lastIndexOf('/') + 1);

/**
 * Moves a video in Vimeo to a given destination folder.
 *
 * @param {File} videoId - The id of the video to be moved.
 * @param {string} coachId - The coach ID.
 * @param {firebase.remote.RemoteType} remote - The Firebase remote object for Firebase-related operations.
 * @returns {Promise<boolean>} Resolves with a boolean indicating whether the operation was successful.
 */
const moveVimeoVideo = async (videoId, coachId, remote) => {
  const response = await remote('moveVimeoVideo', {
    destinationUrl: VIDEO_DESTINATION_URL,
    videoId,
    coachId,
  });

  if (!response.ok) {
    throw new Error('Received an erroneous response');
  }

  const { success } = await response.json();

  if (!success) {
    throw new Error('Something went wrong with moving the video file');
  }

  return success;
};

/**
 * Uploads a video file to Vimeo.
 *
 * This function encompasses the entire process of uploading a video to Vimeo:
 * 1. Creates a video record on Vimeo, preparing for the actual file upload.
 * 2. Uploads the video file using the received upload link.
 * 3. Sends a request to the server to move the uploaded video and resolve once ready.
 *
 * @param {File} file - The video file to be uploaded.
 * @param {string} userFullName - The full name of the user, used in naming the video on Vimeo.
 * @param {string} coachId - The id of the coach.
 * @param {firebase.remote.RemoteType} remote - The Firebase remote object for Firebase-related operations.
 * @returns {Promise<void>} Resolves once all operations (uploading, moving, and waiting) are completed.
 */
const uploadVideoToVimeo = async (file, userFullName, coachId, remote) => {
  const videoName = `${userFullName.replaceAll(' ', '-')}_${file.name}`;
  let videoId = '';
  let errMsg = '';

  try {
    const {
      videoURI,
      uploadLink,
    } = await fetchUploadLink(file.size, videoName, coachId, remote);

    await uploadVideo(uploadLink, file);

    videoId = getVideoIdFromURI(videoURI);
    await moveVimeoVideo(videoId, coachId, remote);
  } catch (error) {
    errMsg = error.message;
  }

  return { videoId, errMsg };
};

/**
 * Enqueues a background task to process video metadata
 *
 * @param {string} videoId - The Vimeo video ID
 * @param {string} coachId - The coach ID
 * @param {string} exerciseId - The exercise document ID
 * @param {firebase.remote.RemoteType} remote - The Firebase remote object for Firebase-related operations.
 * @returns {Promise<boolean>} Resolves with a boolean indicating whether the task was enqueued successfully.
 */
const enqueueVideoProcessing = async (videoId, coachId, exerciseId, remote) => {
  const response = await remote('enqueueVideoForProcessing', {
    videoId,
    coachId,
    exerciseId,
  });

  if (!response.ok) {
    throw new Error('Failed to enqueue video processing task');
  }

  const { success } = await response.json();

  return success;
};

/**
 * Deletes a video from Vimeo. Used to clean up uploaded videos when a coach
 * removes or replaces a video before/after saving.
 *
 * NOTE: requires the `deleteVimeoVideo` server endpoint (S2-8181). Callers
 * should treat this as best-effort cleanup — an abandoned unsaved upload also
 * expires on Vimeo automatically.
 *
 * @param {string} videoId - The id of the video to be deleted.
 * @param {string} coachId - The coach ID.
 * @param {firebase.remote.RemoteType} remote - The Firebase remote object for Firebase-related operations.
 * @returns {Promise<boolean>} Resolves with a boolean indicating whether the operation was successful.
 */
const deleteVimeoVideo = async (videoId, coachId, remote) => {
  const response = await remote('deleteVimeoVideo', {
    videoId,
    coachId,
  });

  if (!response.ok) {
    throw new Error('Something went wrong with deleting the video file');
  }

  const { success } = await response.json();

  if (!success) {
    throw new Error('Something went wrong with deleting the video file');
  }

  return success;
};

const VIMEO_OEMBED_URL = 'https://vimeo.com/api/oembed.json';
const VIMEO_PAGE_URL_REGEX = /^https:\/\/vimeo\.com\/\d+/i;

// One lookup per video URL for the whole session: the workout builder renders the same exercise in
// many rows, and caching the promise also collapses concurrent lookups into a single request.
const deletedVideoLookups = new Map();

// Statuses that settle the question: found (2xx), private but existing (403), or deleted (404).
const isDefiniteOEmbedStatus = ({ ok, status }) => ok || status === 403 || status === 404;

// How long an inconclusive lookup is reused before Vimeo is asked again. The picker remounts rows on scroll,
// so forgetting it at once would re-fire the very requests Vimeo is throttling.
const INCONCLUSIVE_LOOKUP_COOLDOWN_MS = 60 * 1000;

/**
 * Checks whether a Vimeo video no longer exists, using Vimeo's public oEmbed endpoint (no token, CORS open).
 *
 * Only a 404 counts as deleted. Private or embed-restricted videos answer 403 and still play in the app,
 * so any other status, a network error, or a non-Vimeo URL resolves to false rather than flagging a
 * video that may be fine. Definite answers are cached for the session; a failed or rate-limited lookup is
 * reused for a minute and then asked again.
 *
 * @param {string} videoUrl - The exercise's Vimeo page URL (e.g. https://vimeo.com/123456/abcdef).
 * @returns {Promise<boolean>} Resolves true only when Vimeo reports the video as not found.
 */
const isVimeoVideoDeleted = (videoUrl) => {
  if (!VIMEO_PAGE_URL_REGEX.test(videoUrl || '')) {
    return Promise.resolve(false);
  }

  if (!deletedVideoLookups.has(videoUrl)) {
    const forgetAfterCooldown = () => setTimeout(
      () => deletedVideoLookups.delete(videoUrl),
      INCONCLUSIVE_LOOKUP_COOLDOWN_MS,
    );
    const lookup = fetch(`${VIMEO_OEMBED_URL}?url=${encodeURIComponent(videoUrl)}`)
      .then((response) => {
        if (!isDefiniteOEmbedStatus(response)) {
          forgetAfterCooldown();
        }
        return response.status === 404;
      })
      .catch(() => {
        forgetAfterCooldown();
        return false;
      });
    deletedVideoLookups.set(videoUrl, lookup);
  }

  return deletedVideoLookups.get(videoUrl);
};

export {
  fetchUploadLink,
  uploadVideo,
  moveVimeoVideo,
  deleteVimeoVideo,
  getVideoIdFromURI,
  uploadVideoToVimeo,
  enqueueVideoProcessing,
  isVimeoVideoDeleted,
};
